import { Link } from "react-router-dom";
import { Heart, Plus, Star } from "lucide-react";
import { motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { formatNaira } from "@/lib/api";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart } = useCart();
  const { has, toggle } = useWishlist();
  const wishlisted = has(product.id);
  const discount = product.original_price ? Math.round(((product.original_price - product.price) / product.original_price) * 100) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className="group cursor-pointer"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="bg-[#F3EFEA] aspect-[4/5] overflow-hidden rounded-sm mb-4 relative">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          {discount > 0 && (
            <span className="absolute top-3 left-3 bg-[#333E48] text-white text-[10px] uppercase tracking-widest px-2 py-1">
              −{discount}%
            </span>
          )}
          <button
            onClick={(e) => { e.preventDefault(); toggle(product.id); }}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-white flex items-center justify-center transition"
            data-testid={`wishlist-toggle-${product.id}`}
            aria-label="Toggle wishlist"
          >
            <Heart size={16} fill={wishlisted ? "#EF4444" : "none"} color={wishlisted ? "#EF4444" : "#333E48"} />
          </button>
        </div>
      </Link>

      <div className="flex flex-col gap-1 px-1">
        <p className="label-eyebrow">{(product.categories || []).join(" · ")}</p>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-lg leading-tight hover:text-[#6A747D] transition-colors" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>
        </Link>
        {product.rating > 0 && (
          <div className="flex items-center gap-1 text-xs text-[#6A747D]">
            <Star size={12} fill="#FED700" color="#FED700" />
            <span>{product.rating.toFixed(1)}</span>
            <span>({product.review_count})</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-baseline gap-2">
            <span className="font-medium text-[#333E48]" data-testid={`product-price-${product.id}`}>{formatNaira(product.price)}</span>
            {product.original_price && (
              <span className="text-xs text-[#9CA3AF] line-through">{formatNaira(product.original_price)}</span>
            )}
          </div>
          <button
            onClick={() => addToCart(product)}
            className="w-9 h-9 bg-[#FED700] hover:bg-[#E5C200] flex items-center justify-center transition-colors"
            data-testid={`add-to-cart-${product.id}`}
            aria-label="Add to cart"
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
